import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { AlertTriangle, X } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/States';
import { ConfirmDialog, Modal } from '../components/ui/Modal';
import { Field, Textarea } from '../components/ui/Field';
import { Play } from 'lucide-react';
import { NodePalette } from '../features/flows/NodePalette';
import { FlowCanvas } from '../features/flows/FlowCanvas';
import { PropertiesPanel } from '../features/flows/PropertiesPanel';
import { EditorTopBar } from '../features/flows/EditorTopBar';
import { RunPanel } from '../features/runs/RunPanel';
import { useFlowSimulation } from '../features/runs/useFlowSimulation';
import { useFlowStore } from '../store/flowStore';
import { flowsService } from '../services/flowsService';
import { agentsService } from '../services/agentsService';
import { budgetService } from '../services/budgetService';
import { toast } from '../store/uiStore';
import { NODE_DEF_BY_KIND } from '../lib/catalog';
import type { Agent, KnowledgeBase, Person } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function FlowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAgentStudioNavigate();
  const canExecute = useAgentStudioPermissions().has(P.flowsExecute);

  const loadFlow = useFlowStore((s) => s.loadFlow);
  const reset = useFlowStore((s) => s.reset);
  const toFlowDTO = useFlowStore((s) => s.toFlowDTO);
  const markSaved = useFlowStore((s) => s.markSaved);
  const dirty = useFlowStore((s) => s.dirty);
  const meta = useFlowStore((s) => s.meta);
  const selectedNodeId = useFlowStore((s) => s.selectedNodeId);
  const duplicateNode = useFlowStore((s) => s.duplicateNode);
  const storeNodes = useFlowStore((s) => s.nodes);
  const pendingDeleteIds = useFlowStore((s) => s.pendingDeleteIds);
  const cancelDelete = useFlowStore((s) => s.cancelDelete);
  const deleteNode = useFlowStore((s) => s.deleteNode);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [inputOpen, setInputOpen] = useState(false);
  const [runInput, setRunInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [warnDismissed, setWarnDismissed] = useState(false);

  const sim = useFlowSimulation();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      flowsService.get(id),
      agentsService.list(),
      agentsService.listKnowledgeBases(),
      budgetService.listPeople(),
    ]).then(([flow, a, k, p]) => {
      if (flow) loadFlow(flow);
      setAgents(a);
      setKbs(k);
      setPeople(p);
      setLoading(false);
    });
    return () => reset();
  }, [id, loadFlow, reset]);

  const save = useCallback(async () => {
    setSaving(true);
    const saved = await flowsService.save(toFlowDTO());
    markSaved();
    setSaving(false);
    toast.success(`Fluxo "${saved.name}" salvo.`);
  }, [toFlowDTO, markSaved]);

  // Abre o modal de entrada do fluxo.
  const run = useCallback(() => {
    if (!canExecute) return;
    setInputOpen(true);
  }, [canExecute]);

  // Salva o fluxo atual e dispara a execução real com a entrada informada.
  const startRun = useCallback(async () => {
    setInputOpen(false);
    const flow = toFlowDTO();
    try { await flowsService.save(flow); markSaved(); } catch { /* executa o que está no canvas */ }
    setRunOpen(true);
    sim.clear();
    sim.start(flow, runInput);
  }, [toFlowDTO, sim, runInput, markSaved]);

  async function remove() {
    if (!id) return;
    await flowsService.remove(id);
    toast.success('Fluxo excluído.');
    navigate('/flows');
  }

  // Atalhos de teclado (seção 9)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        save();
      } else if (mod && e.key === 'Enter') {
        e.preventDefault();
        run();
      } else if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (selectedNodeId) duplicateNode(selectedNodeId);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, run, selectedNodeId, duplicateNode]);

  if (loading || !meta) {
    return (
      <div className="p-8">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="mt-4 h-[70vh] w-full" />
      </div>
    );
  }

  // Conflito de governança no nível do fluxo (seção 6.7)
  const flowDto = toFlowDTO();
  const hasReviewNode = flowDto.nodes.some((n) => n.kind === 'human_review');
  const sensitiveAgentIds = new Set(
    agents.filter((a) => a.guardrails.humanApprovalActions.length > 0).map((a) => a.id),
  );
  const usesSensitiveAgent = flowDto.nodes.some(
    (n) => n.kind === 'agent' && sensitiveAgentIds.has(n.data.agentId as string),
  );
  const govConflict =
    meta.oversightMode === 'out_of_the_loop' && usesSensitiveAgent && !hasReviewNode && !warnDismissed;

  return (
    <div className="flex h-full flex-col">
      <EditorTopBar
        saving={saving}
        dirty={dirty}
        running={sim.state.running}
        hasRun={sim.state.events.length > 0 || sim.state.running || !!sim.state.finishedRun || sim.state.pausedForHuman}
        onSave={save}
        onRun={run}
        onShowRun={() => setRunOpen(true)}
        onDelete={() => setConfirmDelete(true)}
      />

      {govConflict && (
        <div className="flex items-center gap-2 border-b border-warning/30 bg-warning/10 px-5 py-2 text-sm text-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            Conflito de governança: este fluxo usa um agente com ações sensíveis, mas está em <strong>Human-out-of-the-loop</strong> e não tem nó de revisão. Ação sensível sem ponto de revisão.
          </span>
          <button onClick={() => setWarnDismissed(true)} className="rounded p-1 hover:bg-warning/20" aria-label="Dispensar">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <NodePalette />
        <div className="min-w-0 flex-1">
          <ReactFlowProvider>
            <FlowCanvas />
          </ReactFlowProvider>
        </div>
        <PropertiesPanel agents={agents} kbs={kbs} people={people} />
      </div>

      <RunPanel
        open={runOpen}
        onClose={() => setRunOpen(false)}
        sim={sim}
        oversightMode={meta.oversightMode}
        flowName={meta.name}
      />

      <Modal
        open={inputOpen}
        onClose={() => setInputOpen(false)}
        title="Entrada do fluxo"
        footer={
          <>
            <Button variant="ghost" onClick={() => setInputOpen(false)}>Cancelar</Button>
            <Button variant="primary" icon={Play} onClick={startRun}>Executar</Button>
          </>
        }
      >
        <Field label="O que o fluxo deve processar?" hint="vira a entrada do primeiro nó e segue encadeando" className="mb-0">
          <Textarea
            rows={5}
            value={runInput}
            onChange={(e) => setRunInput(e.target.value)}
            placeholder="ex.: Analisar este pedido de reembolso de R$ 2.500 referente a um produto com defeito…"
            autoFocus
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title="Excluir fluxo"
        message={`Tem certeza que deseja excluir "${meta.name}"?`}
      />

      <ConfirmDialog
        open={pendingDeleteIds.length > 0}
        onClose={cancelDelete}
        onConfirm={() => {
          pendingDeleteIds.forEach((id) => deleteNode(id));
          cancelDelete();
        }}
        title={pendingDeleteIds.length > 1 ? 'Excluir nós' : 'Excluir nó'}
        confirmLabel="Excluir"
        message={deleteMessage(storeNodes, pendingDeleteIds)}
      />
    </div>
  );
}

// Mensagem do modal de exclusão de nó(s), com nome/rótulo quando for um só.
function deleteMessage(
  nodes: ReturnType<typeof useFlowStore.getState>['nodes'],
  ids: string[],
): string {
  if (ids.length > 1) {
    return `Tem certeza que deseja excluir ${ids.length} nós? Esta ação não pode ser desfeita.`;
  }
  const node = nodes.find((n) => n.id === ids[0]);
  const label = node
    ? (node.data.config.label as string) || NODE_DEF_BY_KIND[node.data.kind].label
    : 'este nó';
  const isLoop = node?.data.kind === 'loop';
  return isLoop
    ? `Tem certeza que deseja excluir o loop "${label}"? Os nós dentro dele serão mantidos (saem do loop).`
    : `Tem certeza que deseja excluir o nó "${label}"? Esta ação não pode ser desfeita.`;
}
